import React from 'react';
import { ColorSchemeMapping, Flex, Text } from '@fluentui/react-northstar';
import { UndoIcon } from '@fluentui/react-icons-mdl2';
import { useTranslation } from 'react-i18next';
import { RefreshBar } from './RefreshBar';
import { RefreshIcon } from '../RefreshIcon';

interface Props {
  colorScheme: ColorSchemeMapping;
  label: string;
  /** Reset View and Refresh are drawn only where the list has them. */
  canResetView?: boolean;
  onResetView?: () => void;
  onRefresh?: () => void;
  /** The list's search box, drawn at the right. */
  search: React.ReactNode;
  /** The list below is reloading in place: the loading bar lies on this bar's hairline. */
  refreshing?: boolean;
  style?: React.CSSProperties;
}

// The bar above a list that has no view picker: the list's name, Reset View and Refresh where it has them (as on
// the ticket list's view bar), and the search at the right. No fill, only a hairline under it, so it reads as part of the page.
export const ListViewBar = ({ colorScheme, label, canResetView, onResetView, onRefresh, search, refreshing, style }: Props) => {
  const { t } = useTranslation();
  return (
    <Flex vAlign='center' style={{ position: 'relative', justifyContent: 'space-between', gap: '16px', padding: '6px 10px', borderBottom: `1px solid ${colorScheme.default.border}`, ...style }}>
      {/* Its bottom on the hairline's bottom, so it covers the line rather than sitting under it. */}
      {refreshing && <RefreshBar board style={{ top: 'auto', bottom: -1 }} />}
      <div className='flex items-center space-x-4'>
        <Text content={label} weight='semibold' />
        {(onResetView || onRefresh) && <div style={{ width: '1px', height: '16px', backgroundColor: colorScheme.default.border }} />}
        {/* Real buttons, so the keyboard reaches them; Tailwind's base styles leave a button looking like its text. */}
        {onResetView && <button
          type='button'
          className={`flex font-semibold space-x-1 items-center ${canResetView ? 'cursor-pointer' : 'cursor-default'}`}
          style={{ color: canResetView ? colorScheme.brand.foreground : colorScheme.default.foregroundDisabled }}
          onClick={onResetView}
          disabled={!canResetView}
        >
          <UndoIcon />
          <span>{t('ticket.ticket-list.configure-column.reset-view')}</span>
        </button>}
        {onRefresh && <button
          type='button'
          className='flex font-semibold space-x-1 items-center cursor-pointer'
          style={{ color: colorScheme.brand.foreground }}
          onClick={onRefresh}
        >
          <RefreshIcon outline />
          <span>{t('ticket.toolbar.refresh')}</span>
        </button>}
      </div>
      {search}
    </Flex>
  );
};
